// Formatters per il display di numeri/valute/percentuali.
// Tutti i valori monetari dal backend arrivano come stringa (precisione Decimal).

export function fmtUSD(
  n: number,
  opts: { compact?: boolean; dp?: number } = {},
): string {
  const { compact = true, dp } = opts;
  if (compact && Math.abs(n) >= 1000) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: dp ?? 2,
    minimumFractionDigits: dp ?? 2,
  }).format(n);
}

/** Placeholder per metriche ancora a zero (market appena creati).
 *  Unico punto di verità: card, tabella, hero e dettaglio devono mostrare
 *  lo stesso simbolo. */
export const EMPTY_METRIC = "—";

/** Formatta una metrica monetaria di market (volume / liquidità).
 *  Quasi tutti i market aperti partono da 0: renderizzare "$0.00" sembra
 *  un bug, quindi in quel caso mostriamo un trattino. */
export function fmtMarketMetric(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return EMPTY_METRIC;
  return fmtUSD(n, { compact: true });
}

/** Variante conteggio (numero di trade) con lo stesso trattamento dello zero. */
export function fmtMarketCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return EMPTY_METRIC;
  return fmtNum(n);
}

export function fmtNum(n: number, compact = true): string {
  if (compact && Math.abs(n) >= 1000) {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  }
  return new Intl.NumberFormat("en-US").format(n);
}

export function fmtPct(n: number, dp = 1): string {
  return `${n.toFixed(dp)}%`;
}

export function fmtCents(n: number): string {
  return `${(n * 100).toFixed(1)}¢`;
}

/** Parse string Decimal dal backend a number JS. Per UI/display.
 *  Per arithmetic accurato resta come stringa fino al payload POST. */
export function parseDecimal(s: string | null | undefined): number {
  if (s === null || s === undefined || s === "") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** Calcola il giorno-ora "relativo" per la UI ("2m ago", "3h ago"). */
export function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Date in formato umano. Include l'anno se diverso da quello corrente —
 *  importante per market che risolvono oltre i 6 mesi. */
export function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
